import React, { useState, useEffect, useMemo, useRef } from "react";
import { Modal, Button } from "react-bootstrap";
import { Icon } from "@iconify/react";
import { Link } from "react-router-dom";
import $ from "jquery";
import "datatables.net-dt/js/dataTables.dataTables";
import { NotificationManager } from "react-notifications";
import { useNavigate } from "react-router-dom";

// Helper functions
const formatApiDate = (dateString) => {
  if (!dateString) return null;
  
  if (dateString.includes('T')) {
    const dateObj = new Date(dateString);
    return dateObj.toISOString().split('T')[0];
  }
  
  return dateString;
};

const cleanImageUrl = (url) => {
  if (!url) return null;
  const domain = 'http://api.sidharthsogani.com/uploads/';
  if (url.startsWith(domain)) {
    return domain + url.split(domain).pop();
  }
  return url;
};

const truncateText = (text, length = 50) => {
  if (!text) return 'N/A';
  return text.length > length ? `${text.substring(0, length)}...` : text;
};

const CONTENT_TYPES = ["News", "Interviews", "Articles", "Research", "Blog", "Reels"];

const SUB_TYPES = {};

// Content types that use a combo input (seeded dropdown + "Other (enter new)…")
// instead of a strictly fixed list.
const FREE_TEXT_SUBTYPES = ["Articles", "Reels"];

// Seed list of reputed publications shown in the Articles → Publication dropdown.
// These appear by default and are merged with any custom publications already in use.
const REPUTED_PUBLICATIONS = [
  "Bloomberg",
  "Business Standard",
  "CNBC",
  "CoinDesk",
  "Cointelegraph",
  "Economic Times",
  "Financial Express",
  "Forbes",
  "Fortune",
  "Hindustan Times",
  "LiveMint",
  "Moneycontrol",
  "Reuters",
  "The Hindu BusinessLine",
  "The Wall Street Journal",
  "Times of India",
];

const REEL_SUB_TYPES = ["Shorts", "Videos", "Podcasts"];

const SEED_SUB_TYPES = {
  Articles: REPUTED_PUBLICATIONS,
  Reels: REEL_SUB_TYPES,
};

const hasFixedSubTypes = (contentType) =>
  Array.isArray(SUB_TYPES[contentType]) && SUB_TYPES[contentType].length > 0;

const hasFreeTextSubType = (contentType) =>
  FREE_TEXT_SUBTYPES.includes(contentType);

const hasSubTypes = (contentType) =>
  hasFixedSubTypes(contentType) || hasFreeTextSubType(contentType);

const subTypeLabel = (contentType) =>
  contentType === "Articles" ? "Publication" : "Sub Type";

const MediaManager = () => {
  const navigate = useNavigate();
  const BaseURL = process.env.REACT_APP_API;
  const [mediaList, setMediaList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [modalType, setModalType] = useState("add");
  const [currentMedia, setCurrentMedia] = useState({
    MediaId: "",
    Title: "",
    ContentType: "News",
    SubType: "",
    Description: "",
    SourceURL: "",
    YoutubeVideo: "",
    imageFile: null,
    Image: "",
    Date: new Date().toISOString().split('T')[0]
  });
  const [filterContentType, setFilterContentType] = useState("");
  const [filterSubType, setFilterSubType] = useState("");
  const [useOtherSubType, setUseOtherSubType] = useState(false);
  const tableRef = useRef(null);

  useEffect(() => {
    const token = sessionStorage.getItem("AdminAuthToken");
    if (!token) navigate("/");
  }, [navigate]);

  const fetchMedia = async () => {
    try {
      const token = sessionStorage.getItem("AdminAuthToken");
      const res = await fetch(`${BaseURL}/api/media`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to fetch media");
      const data = await res.json();
      
      const formattedData = data.map(item => ({
        ...item,
        Image: cleanImageUrl(item.Image),
        Date: formatApiDate(item.Date),
        CreatedAt: formatApiDate(item.CreatedAt),
        UpdatedAt: formatApiDate(item.UpdatedAt)
      })).reverse();
      
      setMediaList(formattedData);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMedia();
  }, []);

  const filteredMedia = useMemo(() => {
    return mediaList.filter((m) => {
      if (filterContentType && m.ContentType !== filterContentType) return false;
      if (filterSubType && (m.SubType || "") !== filterSubType) return false;
      return true;
    });
  }, [mediaList, filterContentType, filterSubType]);

  // Returns unique SubType values previously used for the given ContentType.
  // Used to suggest existing publications in the Articles combo input and filter.
  const subTypeIndex = useMemo(() => {
    const index = {};
    for (const m of mediaList) {
      if (!m.ContentType || !m.SubType) continue;
      if (!index[m.ContentType]) index[m.ContentType] = new Set();
      index[m.ContentType].add(m.SubType);
    }
    return index;
  }, [mediaList]);

  const knownSubTypes = (contentType) => {
    if (!contentType) return [];
    const seed = SEED_SUB_TYPES[contentType] || [];
    const used = subTypeIndex[contentType] || new Set();
    const merged = new Set([...seed, ...used]);
    return Array.from(merged).sort((a, b) => a.localeCompare(b));
  };

  useEffect(() => {
    if (filteredMedia.length > 0) {
      const table = $("#dataTable").DataTable({ destroy: true });
      return () => {
        table.destroy();
      };
    }
  }, [filteredMedia]);

  const openModal = (type, media = null) => {
    setModalType(type);
    if (type === "add") {
      setCurrentMedia({
        Title: "",
        ContentType: "News",
        SubType: "",
        Description: "",
        SourceURL: "",
        YoutubeVideo: "",
        imageFile: null,
        Date: new Date().toISOString().split('T')[0]
      });
      setUseOtherSubType(false);
    } else {
      const sub = media.SubType || "";
      setCurrentMedia({
        ...media,
        imageFile: null,
        MediaId: media.MediaId || media.id,
        SubType: sub,
        Date: formatApiDate(media.Date) || new Date().toISOString().split('T')[0],
        Image: cleanImageUrl(media.Image)
      });
      // If editing an Article whose Publication isn't yet in the known list, open in "Other" mode.
      const existing = knownSubTypes(media.ContentType);
      setUseOtherSubType(
        hasFreeTextSubType(media.ContentType) && sub !== "" && !existing.includes(sub)
      );
    }
    setShowModal(true);
  };

  const handleView = (media) => {
    setCurrentMedia({
      ...media,
      imageFile: null,
      MediaId: media.MediaId || media.id,
      SubType: media.SubType || "",
      Date: formatApiDate(media.Date) || new Date().toISOString().split('T')[0],
      Image: cleanImageUrl(media.Image)
    });
    setShowDetailModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "imageFile") {
      setCurrentMedia({ ...currentMedia, imageFile: files[0] });
    } else if (name === "ContentType") {
      setCurrentMedia({
        ...currentMedia,
        ContentType: value,
        SubType: "",
      });
      setUseOtherSubType(false);
    } else {
      setCurrentMedia({ ...currentMedia, [name]: value });
    }
  };

  const handleSubmit = async () => {
    const token = sessionStorage.getItem("AdminAuthToken");

    const { Title, ContentType, Description, Date } = currentMedia;
    if (!Title.trim() || !ContentType.trim() || !Description.trim() || !Date) {
      NotificationManager.error("Please fill in all required fields (Title, Content Type, Description, and Date).");
      return;
    }

    try {
      let res;
      const form = new FormData();
      form.append("Title", currentMedia.Title);
      form.append("ContentType", currentMedia.ContentType);
      form.append("SubType", currentMedia.SubType || "");
      form.append("Description", currentMedia.Description);
      form.append("SourceURL", currentMedia.SourceURL);
      form.append("YoutubeVideo", currentMedia.YoutubeVideo);
      form.append("Date", currentMedia.Date);

      if (modalType === "add") {
        if (currentMedia.imageFile) {
          form.append("image", currentMedia.imageFile);
        }

        res = await fetch(`${BaseURL}/api/media`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: form,
        });
      } 
      else if (modalType === "edit") {
        if (currentMedia.imageFile) {
          form.append("image", currentMedia.imageFile);
        } else if (currentMedia.Image) {
          form.append("existingImage", currentMedia.Image);
        }

        res = await fetch(`${BaseURL}/api/media/${currentMedia.MediaId}`, {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}` },
          body: form,
        });
      } 
      else if (modalType === "delete") {
        res = await fetch(`${BaseURL}/api/media/${currentMedia.MediaId}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      if (!res.ok) throw new Error(`Failed to ${modalType} media`);
      NotificationManager.success(`Media ${modalType === "delete" ? "deleted" : modalType === "add" ? "added" : "updated"} successfully`);
      await fetchMedia();
      setShowModal(false);
    } catch (err) {
      NotificationManager.error(err.message);
    }
  };

  if (loading) return <div className="text-center py-4">Loading...</div>;
  if (error) return <div className="alert alert-danger">Error: {error}</div>;

  return (
    <div className="card basic-data-table">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h5>Media Manager</h5>
          <button className="btn btn-primary" onClick={() => openModal("add")}>
            <Icon icon="mdi:plus" width="16" className="me-1" />
            Add Media
          </button>
        </div>

        <div className="row g-2 mb-3 align-items-end">
          {hasSubTypes(filterContentType) && (
            <div className="col-md-3">
              <label className="form-label mb-1">
                Filter by {subTypeLabel(filterContentType)}
              </label>
              <select
                className="form-select"
                value={filterSubType}
                onChange={(e) => setFilterSubType(e.target.value)}
              >
                <option value="">All</option>
                {(hasFixedSubTypes(filterContentType)
                  ? SUB_TYPES[filterContentType]
                  : knownSubTypes(filterContentType)
                ).map((st) => (
                  <option key={st} value={st}>{st}</option>
                ))}
              </select>
            </div>
          )}
          {(filterContentType || filterSubType) && (
            <div className="col-md-2">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => {
                  setFilterContentType("");
                  setFilterSubType("");
                }}
              >
                Clear filters
              </button>
            </div>
          )}
          <div className="col-md-auto ms-auto text-muted small">
            Showing {filteredMedia.length} of {mediaList.length}
          </div>
        </div>

        <div className="table-responsive">
          {filteredMedia.length > 0 ? (
            <table 
              ref={tableRef}
              className="table bordered-table mb-0" 
              id="dataTable"
              data-page-length={10}
            >
              <thead>
                <tr>
                  <th>#</th>
                  <th>Title</th>
                  <th>Type</th>
                  <th>Sub Type</th>
                  <th>Date</th>
                  <th>Image</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredMedia.map((media, idx) => (
                  <tr key={media.MediaId || media.id}>
                    <td>{idx + 1}</td>
                    <td>{truncateText(media.Title) || 'N/A'}</td>
                    <td>{media.ContentType || 'N/A'}</td>
                    <td>{media.SubType || '-'}</td>
                    <td>{media.Date || 'N/A'}</td>
                    <td>
                      {media.Image ? (
                        <img src={media.Image} alt={media.Title} width="50" className="img-thumbnail" />
                      ) : 'N/A'}
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <Link
                          to="#"
                          onClick={() => handleView(media)}
                          className="w-32-px h-32-px me-8 bg-primary-light text-primary-600 rounded-circle d-inline-flex align-items-center justify-content-center"
                          title="View Details"
                        >
                          <Icon icon="iconamoon:eye-light" />
                        </Link>
                        <Link
                          to="#"
                          onClick={() => openModal("edit", media)}
                          className="w-32-px h-32-px me-8 bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                          title="Edit"
                        >
                          <Icon icon="lucide:edit" />
                        </Link>
                        <Link
                          to="#"
                          onClick={() => openModal("delete", media)}
                          className="w-32-px h-32-px me-8 bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                          title="Delete"
                        >
                          <Icon icon="mingcute:delete-2-line" />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="alert alert-info">
              {mediaList.length === 0
                ? "No media found. Please add some media."
                : "No media matches the selected filters."}
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit/Delete Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {modalType === "add" && "Add New Media"}
            {modalType === "edit" && "Edit Media"}
            {modalType === "delete" && "Confirm Deletion"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalType === "delete" ? (
            <p>Are you sure you want to delete "{currentMedia.Title}"?</p>
          ) : (
            <>
              <div className="mb-3">
                <label className="form-label">Title *</label>
                <input
                  name="Title"
                  className="form-control"
                  value={currentMedia.Title}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Content Type *</label>
                <select
                  name="ContentType"
                  className="form-select"
                  value={currentMedia.ContentType}
                  onChange={handleInputChange}
                  required
                >
                  {CONTENT_TYPES.map((ct) => (
                    <option key={ct} value={ct}>{ct}</option>
                  ))}
                </select>
              </div>
              {hasFixedSubTypes(currentMedia.ContentType) && (
                <div className="mb-3">
                  <label className="form-label">{subTypeLabel(currentMedia.ContentType)}</label>
                  <select
                    name="SubType"
                    className="form-select"
                    value={currentMedia.SubType || ""}
                    onChange={handleInputChange}
                  >
                    <option value="">-- Select --</option>
                    {SUB_TYPES[currentMedia.ContentType].map((st) => (
                      <option key={st} value={st}>{st}</option>
                    ))}
                  </select>
                </div>
              )}
              {hasFreeTextSubType(currentMedia.ContentType) && (
                <div className="mb-3">
                  <label className="form-label">{subTypeLabel(currentMedia.ContentType)}</label>
                  {!useOtherSubType ? (
                    <select
                      className="form-select"
                      value={currentMedia.SubType || ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "__other__") {
                          setUseOtherSubType(true);
                          setCurrentMedia({ ...currentMedia, SubType: "" });
                        } else {
                          setCurrentMedia({ ...currentMedia, SubType: v });
                        }
                      }}
                    >
                      <option value="">{`-- Select ${subTypeLabel(currentMedia.ContentType)} --`}</option>
                      {knownSubTypes(currentMedia.ContentType).map((st) => (
                        <option key={st} value={st}>{st}</option>
                      ))}
                      <option value="__other__">Other (enter new)…</option>
                    </select>
                  ) : (
                    <div className="d-flex gap-2">
                      <input
                        type="text"
                        name="SubType"
                        className="form-control"
                        value={currentMedia.SubType || ""}
                        onChange={handleInputChange}
                        placeholder={`Enter new ${subTypeLabel(currentMedia.ContentType).toLowerCase()}`}
                        autoFocus
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => {
                          setUseOtherSubType(false);
                          setCurrentMedia({ ...currentMedia, SubType: "" });
                        }}
                      >
                        Back to list
                      </button>
                    </div>
                  )}
                </div>
              )}
              <div className="mb-3">
                <label className="form-label">Date *</label>
                <input
                  type="date"
                  name="Date"
                  className="form-control"
                  value={currentMedia.Date}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Image</label>
                <input
                  type="file"
                  name="imageFile"
                  className="form-control"
                  onChange={handleInputChange}
                  accept="image/*"
                />
                {modalType === "edit" && currentMedia.Image && (
                  <div className="mt-2">
                    <small>Current Image:</small>
                    <img 
                      src={currentMedia.Image} 
                      alt="Current" 
                      width="100" 
                      className="d-block img-thumbnail mt-1" 
                    />
                  </div>
                )}
              </div>
              <div className="mb-3">
                <label className="form-label">Description *</label>
                <textarea
                  name="Description"
                  className="form-control"
                  value={currentMedia.Description}
                  onChange={handleInputChange}
                  rows={4}
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Source URL</label>
                <input
                  type="url"
                  name="SourceURL"
                  className="form-control"
                  value={currentMedia.SourceURL}
                  onChange={handleInputChange}
                  placeholder="https://example.com"
                />
              </div>
              <div className="mb-3">
                <label className="form-label">YouTube Video URL</label>
                <input
                  type="url"
                  name="YoutubeVideo"
                  className="form-control"
                  value={currentMedia.YoutubeVideo}
                  onChange={handleInputChange}
                  placeholder="https://youtube.com/watch?v=..."
                />
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button 
            variant={modalType === "delete" ? "danger" : "primary"} 
            onClick={handleSubmit}
          >
            {modalType === "delete" ? "Delete" : "Save Changes"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* View Details Modal */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Media Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label fw-bold">Title</label>
                <p>{currentMedia.Title || 'N/A'}</p>
              </div>
              <div className="mb-3">
                <label className="form-label fw-bold">Content Type</label>
                <p>{currentMedia.ContentType || 'N/A'}</p>
              </div>
              {hasSubTypes(currentMedia.ContentType) && (
                <div className="mb-3">
                  <label className="form-label fw-bold">{subTypeLabel(currentMedia.ContentType)}</label>
                  <p>{currentMedia.SubType || 'N/A'}</p>
                </div>
              )}
              <div className="mb-3">
                <label className="form-label fw-bold">Date</label>
                <p>{currentMedia.Date || 'N/A'}</p>
              </div>
              <div className="mb-3">
                <label className="form-label fw-bold">Source URL</label>
                <p>
                  {currentMedia.SourceURL ? (
                    <a href={currentMedia.SourceURL} target="_blank" rel="noopener noreferrer">
                      {currentMedia.SourceURL}
                    </a>
                  ) : 'N/A'}
                </p>
              </div>
              <div className="mb-3">
                <label className="form-label fw-bold">YouTube Video</label>
                <p>
                  {currentMedia.YoutubeVideo ? (
                    <a href={currentMedia.YoutubeVideo} target="_blank" rel="noopener noreferrer">
                      {currentMedia.YoutubeVideo}
                    </a>
                  ) : 'N/A'}
                </p>
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label fw-bold">Image</label>
                {currentMedia.Image ? (
                  <img 
                    src={currentMedia.Image} 
                    alt={currentMedia.Title} 
                    className="img-fluid rounded"
                  />
                ) : (
                  <p>N/A</p>
                )}
              </div>
            </div>
          </div>
          <div className="mb-3">
            <label className="form-label fw-bold">Description</label>
            <p style={{ whiteSpace: "pre-wrap" }}>{currentMedia.Description || 'N/A'}</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MediaManager;